import type {
  ProviderCollectionsSampleDto,
  ProviderTableCountDto,
  ResultTable as ResultTableDto,
} from '@visualitics-toolkit/shared';
import { useMemo, useState, type ReactNode } from 'react';

import { Badge, Button, EmptyState, Input, Select } from '../components/atoms';
import { ErrorBlock, LoadingBlock, OrgTypeahead, formatDateTime } from '../components/molecules';
import { ResultTable } from '../components/organisms/ResultTable';
import {
  useActiveConnection,
  useProviderCollectionsProviders,
  useProviderCollectionsSample,
  useProviderCollectionsSummary,
} from '../lib/queries';
import { ApiError } from '../lib/api-client';

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ProviderCollectionsPage(): ReactNode {
  const connection = useActiveConnection();
  const [organizationId, setOrganizationId] = useState('');
  const [provider, setProvider] = useState('');
  const [targetConnectionId, setTargetConnectionId] = useState('');
  const [date, setDate] = useState(todayIsoDate);
  const [selectedTable, setSelectedTable] = useState<ProviderTableCountDto | null>(null);

  const providersQuery = useProviderCollectionsProviders(organizationId || undefined, connection?.id);

  const summaryFilter = useMemo(
    () => ({
      organizationId: organizationId || undefined,
      provider: provider || undefined,
      date: date || undefined,
      targetConnectionId: targetConnectionId || undefined,
    }),
    [organizationId, provider, date, targetConnectionId],
  );

  const summary = useProviderCollectionsSummary(summaryFilter, connection?.id);

  const sampleFilter = useMemo(
    () => ({
      organizationId: organizationId || undefined,
      provider: provider || undefined,
      date: date || undefined,
      schema: selectedTable?.schema,
      table: selectedTable?.table,
      targetConnectionId: targetConnectionId || undefined,
      limit: 50,
    }),
    [organizationId, provider, date, selectedTable, targetConnectionId],
  );

  const sample = useProviderCollectionsSample(sampleFilter, connection?.id);

  const providerOptions = useMemo(() => {
    const rows = providersQuery.data?.providers ?? [];
    const services = [...new Set(rows.map((row) => row.service))].sort();
    return { services, rows };
  }, [providersQuery.data]);

  const connectionsForProvider = useMemo(
    () => providerOptions.rows.filter((row) => row.service === provider),
    [providerOptions.rows, provider],
  );

  if (!connection) {
    return (
      <EmptyState
        title="Nenhuma conexão ativa"
        description="Ative uma conexão para inspecionar coletas de providers no banco alvo."
      />
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-content">Coletas de providers</h1>
            <Badge tone="accent">{connection.name}</Badge>
          </div>
          <p className="text-sm text-muted">
            Escolha organização, provider e data para ver ingest runs, jobs e tabelas alimentadas
            em <code className="text-xs">providers_raw</code>.
          </p>
        </div>
        <Button
          onClick={() => {
            void summary.refetch();
            if (selectedTable) void sample.refetch();
          }}
          loading={summary.isFetching && !summary.isLoading}
          disabled={!organizationId || !provider || !date}
        >
          Atualizar
        </Button>
      </header>

      <section className="grid gap-3 rounded-lg border border-border bg-surface-raised p-4 md:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1 text-sm">
          <span className="text-muted">Organização</span>
          <OrgTypeahead
            value={organizationId}
            onChange={(id) => {
              setOrganizationId(id);
              setProvider('');
              setTargetConnectionId('');
              setSelectedTable(null);
            }}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span className="text-muted">Provider</span>
          <Select
            value={provider}
            disabled={!organizationId || providersQuery.isLoading}
            onChange={(event) => {
              setProvider(event.target.value);
              setTargetConnectionId('');
              setSelectedTable(null);
            }}
          >
            <option value="">
              {!organizationId
                ? 'Selecione a organização'
                : providersQuery.isLoading
                  ? 'Carregando…'
                  : 'Selecione o provider'}
            </option>
            {providerOptions.services.map((service) => (
              <option key={service} value={service}>
                {service}
              </option>
            ))}
          </Select>
        </label>

        <label className="space-y-1 text-sm">
          <span className="text-muted">Connection (opcional)</span>
          <Select
            value={targetConnectionId}
            disabled={!provider}
            onChange={(event) => {
              setTargetConnectionId(event.target.value);
              setSelectedTable(null);
            }}
          >
            <option value="">Todas as connections</option>
            {connectionsForProvider.map((row) => (
              <option key={row.connectionId} value={row.connectionId}>
                {row.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="space-y-1 text-sm">
          <span className="text-muted">Data (snapshot)</span>
          <Input
            type="date"
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
              setSelectedTable(null);
            }}
          />
        </label>
      </section>

      {providersQuery.isError && (
        <ErrorBlock message={toErrorMessage(providersQuery.error)} />
      )}

      {!organizationId || !provider || !date ? (
        <EmptyState
          title="Preencha os filtros"
          description="Organização, provider e data são obrigatórios para carregar o resumo."
        />
      ) : summary.isLoading ? (
        <LoadingBlock label="Carregando coletas…" />
      ) : summary.isError ? (
        <ErrorBlock message={toErrorMessage(summary.error)} />
      ) : summary.data ? (
        <>
          <p className="text-sm text-muted">
            {summary.data.organizationName} · {summary.data.provider} · {summary.data.date}
            {summary.data.connectionId ? ` · connection ${summary.data.connectionId}` : ''}
          </p>

          <Panel title={`Ingest runs (${summary.data.ingestRuns.length})`}>
            {summary.data.ingestRuns.length === 0 ? (
              <p className="text-sm text-muted">Nenhum `provider_ingest_runs` neste dia.</p>
            ) : (
              <ResultTable
                table={toResultTable(
                  'Ingest runs',
                  summary.data.ingestRuns.map((run) => ({
                    dataset: run.dataset,
                    status: run.status,
                    dataset_state: run.datasetState,
                    rows_valid: run.rowsValid,
                    rows_error: run.rowsError,
                    rows_total: run.rowsTotal,
                    started_at: run.startedAt,
                    finished_at: run.finishedAt,
                    error: run.error,
                    connection_id: run.connectionId,
                  })),
                )}
              />
            )}
          </Panel>

          <Panel title={`Collection jobs (${summary.data.collectionJobs.length})`}>
            {summary.data.collectionJobs.length === 0 ? (
              <p className="text-sm text-muted">Nenhum `collection_jobs` neste dia.</p>
            ) : (
              <ResultTable
                table={toResultTable(
                  'Collection jobs',
                  summary.data.collectionJobs.map((job) => ({
                    id: job.id,
                    status: job.status,
                    progress: job.progress,
                    data_types: job.dataTypes.join(', '),
                    started_at: job.startedAt,
                    completed_at: job.completedAt,
                    created_at: job.createdAt,
                    error: job.error,
                  })),
                )}
              />
            )}
          </Panel>

          <Panel title={`Tabelas alimentadas (${summary.data.tables.length})`}>
            {summary.data.tables.length === 0 ? (
              <p className="text-sm text-muted">
                Nenhuma tabela `providers_raw` com linhas neste dia para o provider.
              </p>
            ) : (
              <div className="overflow-auto rounded border border-border">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-surface text-xs uppercase text-muted">
                    <tr>
                      <th className="px-3 py-2">Schema</th>
                      <th className="px-3 py-2">Tabela</th>
                      <th className="px-3 py-2">Linhas</th>
                      <th className="px-3 py-2">Coluna data</th>
                      <th className="px-3 py-2">Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.data.tables.map((row) => {
                      const active =
                        selectedTable?.schema === row.schema && selectedTable?.table === row.table;
                      return (
                        <tr
                          key={`${row.schema}.${row.table}`}
                          className={
                            active
                              ? 'cursor-pointer bg-neon/10'
                              : 'cursor-pointer hover:bg-surface'
                          }
                          onClick={() => setSelectedTable(row)}
                        >
                          <td className="px-3 py-2 font-mono text-xs">{row.schema}</td>
                          <td className="px-3 py-2 font-mono text-xs">{row.table}</td>
                          <td className="px-3 py-2 font-mono">{row.rowCount}</td>
                          <td className="px-3 py-2 font-mono text-xs">{row.dateColumn ?? '—'}</td>
                          <td className="px-3 py-2">
                            {row.downstream ? (
                              <Badge tone="warning">Industry</Badge>
                            ) : (
                              <Badge tone="accent">raw</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-xs text-muted">
              Clique numa linha para carregar amostra (até 50 registros).
            </p>
          </Panel>

          {selectedTable && (
            <Panel
              title={`Amostra · ${selectedTable.schema}.${selectedTable.table}`}
              extra={
                <Button variant="ghost" onClick={() => setSelectedTable(null)}>
                  Fechar
                </Button>
              }
            >
              {sample.isLoading ? (
                <LoadingBlock label="Carregando amostra…" />
              ) : sample.isError ? (
                <ErrorBlock message={toErrorMessage(sample.error)} />
              ) : sample.data ? (
                <SamplePanel sample={sample.data} />
              ) : null}
            </Panel>
          )}
        </>
      ) : null}
    </div>
  );
}

function SamplePanel({ sample }: { sample: ProviderCollectionsSampleDto }): ReactNode {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted">
        {sample.rows.length} linha(s)
        {sample.truncated ? ` (truncado em ${sample.limit})` : ''} · coluna data:{' '}
        {sample.dateColumn ?? '—'} · capturado {formatDateTime(new Date().toISOString())}
      </p>
      {sample.rows.length === 0 ? (
        <p className="text-sm text-muted">Sem linhas para estes filtros.</p>
      ) : (
        <ResultTable table={toResultTable(`Sample ${sample.schema}.${sample.table}`, sample.rows)} />
      )}
    </div>
  );
}

function Panel({
  title,
  children,
  extra,
}: {
  title: string;
  children: ReactNode;
  extra?: ReactNode;
}): ReactNode {
  return (
    <section className="space-y-3 rounded-lg border border-border bg-surface-raised p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-content">{title}</h2>
        {extra}
      </div>
      {children}
    </section>
  );
}

function toResultTable(label: string, rows: Record<string, unknown>[]): ResultTableDto {
  const columns =
    rows.length === 0
      ? []
      : [...new Set(rows.flatMap((row) => Object.keys(row)))];
  return { label, columns, rows, rowCount: rows.length };
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}
